import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { EffectCreative } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import SlideContentHandler from '@/components/atomic/molecules/utils/portals/mobile-menu-container/slide-content-handler';
import MobileWrapper from '@/components/atomic/molecules/utils/portals/mobile-menu-container/styled';
import { MobileMenuContainerProps } from '@/components/atomic/molecules/utils/portals/mobile-menu-container/typings';
import withExposer from '@/hocs/with-exposer';
import useDebounceStore from '@/hooks/use-debounce-store';
import useStore from '@/state';
import getAnimationDirection from '@/state/selectors/navigation/get-animation-direction.ts';
import getAnimationDuration from '@/state/selectors/navigation/get-animation-duration';
import getDropdownSegments from '@/state/selectors/navigation/get-dropdown-segments';
import popDropdownSegmentsAction from '@/state/selectors/navigation/pop-dropdown-segment';

const MobileMenuContainerComponent: React.FC<MobileMenuContainerProps> = () => {
	const animationDuration = useStore(getAnimationDuration);
	const animationDirection = useStore(getAnimationDirection);
	const ref = useRef<SwiperClass>();
	const [dropdownSegments, debouncedDropdownSegments] = useDebounceStore(
		getDropdownSegments,
		animationDuration,
	);
	const popDropdownSegments = useStore(popDropdownSegmentsAction);

	const segments = useMemo(() => {
		if (!dropdownSegments && debouncedDropdownSegments) {
			return debouncedDropdownSegments;
		}
		if (dropdownSegments && !debouncedDropdownSegments) {
			return dropdownSegments;
		}
		if (debouncedDropdownSegments.length > dropdownSegments.length) {
			ref.current?.slidePrev();
			return debouncedDropdownSegments;
		}
		return dropdownSegments;
	}, [debouncedDropdownSegments, dropdownSegments]);

	const onSwiper = useCallback(
		(swiper: SwiperClass) => (ref.current = swiper),
		[],
	);

	useEffect(() => {
		if (ref.current?.activeIndex !== segments.length - 1) {
			ref.current?.slideTo(segments.length - 1);
		}
	}, [segments]);

	useEffect(() => {
		ref.current?.off('slideChange');
		ref.current?.on('slideChange', () => {
			if (ref.current?.swipeDirection === 'prev') {
				popDropdownSegments();
			}
		});
	}, [segments, segments.length, popDropdownSegments]);

	return (
		<MobileWrapper>
			<Swiper
				initialSlide={segments.length - 1}
				onSwiper={onSwiper}
				modules={[EffectCreative]}
				allowTouchMove={true}
				grabCursor={true}
				effect='creative'
				resistanceRatio={0}
				watchSlidesProgress={true}
				threshold={10}
				enabled={animationDirection !== 'right'}
				speed={animationDuration}
				creativeEffect={{
					prev: {
						shadow: true,
						translate: ['-20%', 0, -1],
					},
					next: {
						translate: ['100%', 0, 0],
					},
				}}
			>
				{segments.map((segment, index) => {
					return (
						<SwiperSlide key={segment}>
							<SlideContentHandler
								index={index}
								segment={segment}
							/>
						</SwiperSlide>
					);
				})}
			</Swiper>
		</MobileWrapper>
	);
};

const MobileMenuContainer = memo(
	withExposer(MobileMenuContainerComponent, 'mobileMenu'),
);

export default MobileMenuContainer;
